import { useState } from "react";
import { useUpdateMeetingNotes } from "../../hooks/useMeetings";
import { colors } from "../../style";
import { SentimentChip } from "../records/RecordList";
import {
  apiPlaceholder,
  formatMeetingDate,
  formatSeconds,
} from "../../utils/meetingFormat";
import {
  Btn,
  Card,
  errorMessage,
  Icon,
  LinkButton,
  SectionLabel,
  Spinner,
  ui,
} from "../ui";

const NOTES_MAX = 20000;

const body = { margin: 0, fontSize: "14px", lineHeight: 1.6 };

// Calls can come back with notes but no summary paragraph, so they pass
// hasSummary themselves
export function SummaryTab({
  meeting,
  loading,
  canResummarise,
  onResummarise,
  hasSummary = !!meeting.summary,
  subject = "meeting",
  emptyText = `There's no summary for this ${subject}. The transcript may be too short.`,
}) {
  const writing = meeting.status.value === "SUMMARISING";
  const notes = meeting.meeting_notes ?? [];

  const resummariseLink = canResummarise && (
    <LinkButton onClick={onResummarise}>
      <Icon name="refresh" size={12} />
      {meeting.summary ? "Re-summarise with another template" : "Summarise"}
    </LinkButton>
  );

  if (!hasSummary) {
    return (
      <Card gap={10} style={{ alignItems: "flex-start" }}>
        {(writing || loading) && <Spinner />}
        {!loading && (
          <p style={{ ...body, ...ui.muted }}>
            {writing
              ? "Writing the summary and notes. This updates by itself."
              : emptyText}
          </p>
        )}
      </Card>
    );
  }

  const sentimentLine = meeting.sentiment && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        flexWrap: "wrap",
      }}
    >
      <SentimentChip
        sentiment={{ rating: meeting.sentiment.rating, summary: null }}
        neutralIfUnknown
      />
      {meeting.sentiment.summary &&
        !apiPlaceholder(meeting.sentiment.summary) && (
          <span style={ui.muted}>{meeting.sentiment.summary}</span>
        )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {meeting.summary ? (
        <Card
          title="Summary"
          actions={notes.length === 0 && resummariseLink}
          gap={10}
        >
          <p style={{ ...body, whiteSpace: "pre-line" }}>{meeting.summary}</p>
          {sentimentLine}
        </Card>
      ) : (
        sentimentLine
      )}

      {notes.length > 0 ? (
        <Card title="Notes" actions={resummariseLink} gap={10}>
          {notes.map((note, i) => (
            <NoteSection key={i} note={note} />
          ))}
        </Card>
      ) : (
        loading && <Spinner />
      )}
    </div>
  );
}

// A lone line reads as a paragraph, otherwise bullets (as MeetingNotesList)
function NoteSection({ note }) {
  const content = (note.content ?? []).filter(Boolean);
  if (!note.header && content.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {note.header && (
        <SectionLabel style={{ marginTop: "6px" }}>{note.header}</SectionLabel>
      )}
      {content.length <= 1 || !note.header ? (
        <p style={body}>{content.join(" ")}</p>
      ) : (
        <ul style={{ ...body, paddingLeft: "18px" }}>
          {content.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The rough notes typed while recording. A re-summarise picks up edits.
export function UserNotesCard({ meeting, onResummarise }) {
  const update = useUpdateMeetingNotes(meeting.sqid);
  const [draft, setDraft] = useState(null);
  const [edited, setEdited] = useState(false);
  const notes = meeting.user_notes ?? "";

  const save = async () => {
    try {
      await update.mutateAsync(draft);
      setEdited(draft.trim() !== notes.trim());
      setDraft(null);
    } catch {
      // shown below from update.error
    }
  };

  if (draft === null && !notes) {
    return (
      <LinkButton
        onClick={() => setDraft("")}
        style={{ alignSelf: "flex-start" }}
      >
        <Icon name="pencil" size={12} />
        Add your own notes
      </LinkButton>
    );
  }

  return (
    <Card
      title="Your notes"
      actions={
        draft === null && (
          <LinkButton onClick={() => setDraft(notes)}>
            <Icon name="pencil" size={12} />
            Edit
          </LinkButton>
        )
      }
      style={{ fontSize: "13px" }}
    >
      {draft === null ? (
        <p style={{ margin: 0, whiteSpace: "pre-line", lineHeight: 1.6 }}>
          {notes}
        </p>
      ) : (
        <>
          <textarea
            rows={8}
            value={draft}
            maxLength={NOTES_MAX}
            autoFocus
            aria-label="Your notes"
            onChange={(e) => setDraft(e.target.value)}
            style={{ ...ui.input, resize: "vertical", lineHeight: 1.5 }}
          />
          {update.error && <p style={ui.error}>{errorMessage(update.error)}</p>}
          <div style={{ display: "flex", gap: "8px" }}>
            <Btn variant="primary" loading={update.isLoading} onClick={save}>
              Save notes
            </Btn>
            <Btn variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Btn>
          </div>
        </>
      )}
      {edited && draft === null && onResummarise && (
        <p style={{ ...ui.muted, margin: 0, fontSize: "12px" }}>
          The summary was written before this change.{" "}
          <LinkButton onClick={onResummarise} style={{ fontSize: "12px" }}>
            Re-summarise
          </LinkButton>{" "}
          to include it.
        </p>
      )}
    </Card>
  );
}

export function EmailedCard({ meeting }) {
  const recap = meeting.email_recap;
  if (!recap?.emails?.length) return null;
  const who = recap.emails.join(", ");
  return (
    <Card title="Emailed" style={{ fontSize: "13px" }}>
      <p style={{ margin: 0, lineHeight: 1.6, wordBreak: "break-word" }}>
        {recap.sent_at
          ? `Sent to ${who}. Last sent ${formatMeetingDate(recap.sent_at)}.`
          : `Will go to ${who} once the summary is ready.`}
      </p>
    </Card>
  );
}

// Bookmarks jump to that point in the transcript, using its turn start times
export function BookmarkList({ meeting, timed, onJump, variant }) {
  const bookmarks = meeting.bookmarks ?? [];
  if (!bookmarks.length) return null;
  const title = timed
    ? "Show in the transcript"
    : "This transcript has no timings to jump to";

  if (variant === "chips") {
    return (
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
        {bookmarks.map((b, i) => (
          <button
            key={i}
            type="button"
            title={title}
            disabled={!timed}
            onClick={() => onJump(b.at)}
            style={{
              ...bookmarkChip,
              cursor: timed ? "pointer" : "default",
            }}
          >
            ★ {formatSeconds(b.at)}
            {b.label && (
              <span
                style={{
                  maxWidth: "220px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {b.label}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <Card style={{ padding: "12px" }}>
      <SectionLabel style={{ padding: "4px 10px 6px" }}>Bookmarks</SectionLabel>
      {bookmarks.map((b, i) => (
        <button
          key={i}
          type="button"
          className="sk-row"
          title={title}
          disabled={!timed}
          onClick={() => onJump(b.at)}
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            width: "100%",
            padding: "8px 10px",
            borderRadius: "9px",
            border: 0,
            background: "none",
            color: "inherit",
            font: "inherit",
            fontSize: "13px",
            textAlign: "left",
            cursor: timed ? "pointer" : "default",
          }}
        >
          <span style={{ ...bookmarkChip, border: 0 }}>
            ★ {formatSeconds(b.at)}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>{b.label || "Bookmark"}</span>
          {timed && (
            <span style={{ ...ui.muted, fontSize: "12px" }}>Transcript ›</span>
          )}
        </button>
      ))}
    </Card>
  );
}

const bookmarkChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "3px 9px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  background: colors.amberTint,
  color: colors.amberText,
  border: `1px solid ${colors.amberBorder}`,
  fontFamily: "inherit",
};
